import { Sparkles } from "lucide-react";
import { WaitlistCard } from "@/components/WaitlistCard";

/**
 * Public development landing.
 *
 * Rendered when MAINTENANCE_MODE is ON either at build-time
 * (REACT_APP_MAINTENANCE_MODE=true) or via the runtime /api/config
 * response. During this window the entire public product surface
 * (Landing, /kayit, /demo, /panel/*, /tanilama/*) collapses to this
 * single page. Auth/legal/admin routes stay reachable — see App.js
 * for the exact allow-list.
 *
 * Copy is deliberate and locked (do NOT reword without owner sign-off):
 *   * Frames scope-expansion (not "under maintenance" / "bug fixes").
 *   * Never claims a launch date.
 *   * Never surfaces Corpus, Question Engine, methodology,
 *     misconception engine, or any internal IP / roadmap detail.
 *
 * Waitlist capture reuses the existing WaitlistCard component which
 * hits POST /api/waitlist/join — no new backend surface. Attribution
 * (UTM / campaign / landing_path) is preserved end-to-end. Analytics
 * scripts in index.html continue to load unchanged so GA4 / Clarity /
 * PostHog conversion tracking on this landing still fires.
 */
export default function MaintenancePage() {
  return (
    <div
      data-testid="maintenance-page"
      className="min-h-screen w-full relative overflow-hidden bg-white text-zinc-900 flex flex-col"
      style={{
        fontFamily:
          '"Outfit", "Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      {/* Subtle cobalt glow — matches production brand aesthetic. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(1100px 600px at 20% 0%, rgba(30,58,138,0.08), transparent 60%), radial-gradient(900px 500px at 100% 100%, rgba(30,64,175,0.05), transparent 60%)",
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.025] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />

      <header className="relative z-10 w-full px-6 sm:px-10 lg:px-16 pt-8 sm:pt-10">
        <div className="flex items-center gap-2.5" data-testid="maintenance-logo">
          <div className="w-9 h-9 rounded-xl bg-[#1E3A8A] flex items-center justify-center shadow-[0_8px_20px_rgba(30,58,138,0.20)]">
            <Sparkles className="w-5 h-5 text-white" strokeWidth={2.4} />
          </div>
          <span className="text-lg font-semibold tracking-tight text-zinc-900">
            SuperZeka
          </span>
        </div>
      </header>

      <main className="relative z-10 flex-1 flex items-center">
        <div className="max-w-3xl w-full mx-auto px-6 sm:px-10 lg:px-16 py-16">
          <h1
            data-testid="maintenance-headline"
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight text-zinc-900"
            style={{ fontFamily: '"Outfit", sans-serif' }}
          >
            SUPERZEKA <span className="text-[#1E3A8A]">GELİŞİYOR.</span>
          </h1>

          <p
            data-testid="maintenance-description"
            className="mt-8 max-w-2xl text-lg sm:text-xl text-zinc-700 leading-relaxed"
          >
            SuperZeka&apos;yı yalnızca sorularına cevap veren bir yapay zekâ olarak
            değil,{" "}
            <strong className="font-semibold text-[#1E3A8A]">nasıl öğrendiğini</strong>
            {" anlayan daha kapsamlı bir eğitim deneyimi olarak geliştiriyoruz."}
          </p>
          <p
            data-testid="maintenance-description-2"
            className="mt-5 max-w-2xl text-lg sm:text-xl text-zinc-700 leading-relaxed"
          >
            Bu nedenle platformu, yeni SuperZeka tamamlanana kadar geçici olarak
            erişime kapattık.
          </p>

          <p
            data-testid="maintenance-tagline"
            className="mt-6 text-base sm:text-lg font-medium text-[#1E3A8A]"
          >
            Daha güçlü. Daha kişisel. Daha kapsamlı.
          </p>

          <p
            data-testid="maintenance-closing"
            className="mt-6 text-base sm:text-lg text-zinc-800"
          >
            Yeni SuperZeka yakında.
          </p>

          <p
            data-testid="maintenance-cta"
            className="mt-10 text-sm text-zinc-600 max-w-xl"
          >
            Yeni sürüm açıldığında haber almak için ön kayıt bırakabilirsin.
          </p>
        </div>
      </main>

      {/* Waitlist — reuses existing component + endpoint; captures UTM. */}
      <div className="relative z-10" data-testid="maintenance-waitlist">
        <WaitlistCard />
      </div>

      <footer className="relative z-10 border-t border-zinc-100">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 lg:px-16 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-zinc-500">
          <div>© {new Date().getFullYear()} SuperZeka</div>
          <div className="flex items-center gap-3">
            <a
              href="/gizlilik"
              className="hover:text-zinc-900 transition-colors"
              data-testid="maintenance-privacy-link"
            >
              Gizlilik
            </a>
            <span aria-hidden="true">·</span>
            <a
              href="/kullanim-sartlari"
              className="hover:text-zinc-900 transition-colors"
              data-testid="maintenance-terms-link"
            >
              Kullanım Şartları
            </a>
            <span aria-hidden="true">·</span>
            <span>superzeka.com</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
